<template>
  <div class="relative flex w-full" :class="{ 'flex-col': isSidebarLayout }">
    <!-- Mascot positioned outside top right -->
    <div
      class="order-1 justify-end self-end pb-6 md:self-start"
      :class="{ 'order-[0] md:self-end': isSidebarLayout }"
    >
      <mascot-display
        :mascot-ids="['hönrietta']"
        layout="horizontal"
        :size="props.variant === 'sidebar' ? 'medium' : 'medium'"
        variant="happy"
      />
    </div>

    <!-- Quiz Container -->
    <div
      class="flex w-full overflow-hidden rounded-bl-[20px] rounded-br-[20px] bg-white md:rounded-tl-[20px]"
      :class="{ 'flex-col rounded-tl-[20px]': isSidebarLayout }"
    >
      <!-- Quiz Content -->
      <div
        class="flex flex-col bg-blue-lightest text-center"
        :class="isSidebarLayout ? 'order-2 p-6' : 'p-9 px-6 sm:w-[60%]'"
      >
        <!-- Question State -->
        <div v-if="!showAnswer && isClient && !initFailed">
          <h3 class="mb-6 text-center font-headline text-h4">Välj ditt svar</h3>
          <div
            class="grid grid-cols-1 gap-3"
            :class="
              isSidebarLayout ? 'md:grid-cols-1 lg:grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-2'
            "
          >
            <app-button
              v-for="(option, index) in currentQuestion.options"
              :key="index"
              type="radio"
              variant="rosa"
              :checked="selectedAnswer === option"
              size="md"
              class="answer-button w-full"
              @click="selectAnswer(option)"
            >
              {{ option.name }}
            </app-button>
          </div>
          <app-button
            v-if="selectedAnswer"
            variant="ljusrosa"
            size="desktop"
            position="right"
            arrow-type="double"
            class="mt-6 w-full"
            :class="isSidebarLayout ? '!w-full' : ''"
            @click="revealAnswer"
          >
            Välj & se svar
          </app-button>
        </div>

        <!-- Answer State -->
        <div v-else-if="showAnswer && isClient" class="answer-state md:px-6 lg:px-12">
          <div class="mb-6">
            <div v-if="isCorrect" class="">
              <h4 class="mb-6 text-center font-headline text-h4">
                Wohoo, helt rätt! Läs mer om {{ currentQuestion.correctAnswer.name }} här:
              </h4>
            </div>
            <div v-else class="">
              <h4 class="mb-6 text-center font-headline text-h4">
                Tyvärr, fel. Rätt svar är {{ currentQuestion.correctAnswer.name }}. Läs mer om dem
                här:
              </h4>
            </div>
          </div>

          <!-- Animal Card using ContentCard component -->
          <div
            class="m-auto max-w-[390px] transition-all duration-300 ease-in-out hover:-translate-y-1"
          >
            <content-card
              v-if="correctAnimalData"
              :animal="correctAnimalData"
              :link="currentQuestion.correctAnswer.url"
            />
          </div>
        </div>
      </div>

      <!-- Quiz Header -->
      <div
        class="flex flex-col items-center justify-center text-left"
        :class="isSidebarLayout ? 'order-1 p-6' : 'p-9 sm:w-[40%]'"
      >
        <div>
          <h2
            class="mb-4 text-left font-headline"
            :class="isSidebarLayout ? 'text-h3 text-purple-dark' : 'text-h3'"
          >
            Hej! Jag heter Hönrietta och jag vill testa dina kunskaper i Djurquiz!
          </h2>
          <p v-if="initFailed" class="text-body text-slate-darker">
            Quizet kunde inte laddas just nu. Försök igen senare!
          </p>
          <p v-else class="text-body text-slate-darker">
            {{ isClient ? currentQuestion.question : 'Laddar fråga...' }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
defineOptions({
  name: 'QuizBlock',
})

interface Props {
  variant?: 'default' | 'sidebar'
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
})

// Screen size detection
const isMobile = ref(false)

// Update screen size on mount and resize
onMounted(() => {
  const updateScreenSize = () => {
    isMobile.value = window.innerWidth < 768 // md breakpoint
  }

  updateScreenSize()
  window.addEventListener('resize', updateScreenSize)

  onUnmounted(() => {
    window.removeEventListener('resize', updateScreenSize)
  })
})

// Computed property that determines if we should use sidebar layout
const isSidebarLayout = computed(() => {
  return props.variant === 'sidebar' || (props.variant === 'default' && isMobile.value)
})

interface QuizOption {
  name: string
  url: string
  image: string
}

interface QuizQuestion {
  question: string
  correctAnswer: QuizOption
  options: QuizOption[]
}

interface QuizAnimal {
  slug: string
  name: string
  url: string
  image: string
  title: string
  kicker: string
  species: string
  'facts-block': { facts: string[] }
}

interface QuizDataPayload {
  questions: Array<{
    question: string
    correctAnimal: QuizAnimal
  }>
  allAnimals: Array<{
    slug: string
    name: string
    url: string
    image: string
  }>
}

// Quiz data is pre-validated at build time — all questions have matching animals
const quizData = await useStatic('quiz-data', () => $fetch<QuizDataPayload>('/quiz-data.json'))

// Reactive state
const currentQuestion = ref<QuizQuestion>({
  question: '',
  correctAnswer: { name: '', url: '', image: '' },
  options: [],
})
const selectedAnswer = ref<QuizOption | null>(null)
const showAnswer = ref(false)
const isCorrect = ref(false)
const correctAnimalData = ref<QuizAnimal | null>(null)
const isClient = ref(false)
const initFailed = ref(false)

// Initialize quiz with random question selection
const initializeQuiz = () => {
  const { questions, allAnimals } = quizData

  if (questions.length === 0) {
    initFailed.value = true
    return
  }

  const randomIndex = Math.floor(Math.random() * questions.length)
  const questionData = questions[randomIndex]
  if (!questionData) {
    initFailed.value = true
    return
  }

  const correctAnswer: QuizOption = {
    name: questionData.correctAnimal.name,
    url: questionData.correctAnimal.url,
    image: questionData.correctAnimal.image,
  }

  // Get 5 random wrong answers from all animals (excluding the correct one)
  const otherAnimals = allAnimals.filter((a) => a.slug !== questionData.correctAnimal.slug)
  const shuffled = [...otherAnimals].sort(() => Math.random() - 0.5)
  const wrongAnswers: QuizOption[] = shuffled.slice(0, 5).map((a) => ({
    name: a.name,
    url: a.url,
    image: a.image,
  }))

  const allOptions: QuizOption[] = [correctAnswer, ...wrongAnswers].sort(() => Math.random() - 0.5)

  currentQuestion.value = {
    question: questionData.question,
    correctAnswer,
    options: allOptions,
  }
  correctAnimalData.value = questionData.correctAnimal
  isClient.value = true
}

// Select an answer
const selectAnswer = (option: QuizOption) => {
  selectedAnswer.value = option
}

// Reveal the answer
const revealAnswer = () => {
  if (!selectedAnswer.value) return

  showAnswer.value = true
  isCorrect.value = selectedAnswer.value.url === currentQuestion.value.correctAnswer.url
}

// Initialize on mount — quiz data is guaranteed valid from build-time validation
onMounted(() => {
  initializeQuiz()
})
</script>

<style scoped>
/* Animation for answer reveal */
.answer-state {
  animation: slideIn 0.5s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
